import React, { useState, useRef, useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPaperPlane,
    faWindowMinimize,
    faCommentDots,
    faThumbsUp,
    faThumbsDown,
    faXmark,
    faCheck,
    faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import gfm from "remark-gfm";
import { Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Chat from "./Chat";

const ChatWindow = ({
    apiBaseUrl,
    botId,
    initialMessage = "",
    initialMessageIsSilent = false,
    chatContext = [],
    settings,
}) => {
    const [isMinimized, setIsMinimized] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const chatRef = useRef(null);
    const initialMessageSentRef = useRef(false);

    // Get title and description from settings if available
    const headerTitle = settings?.styling?.title || "Chat Window";
    const headerDescription = settings?.styling?.description;

    const botAvatar =
        "https://api.dicebear.com/7.x/initials/svg?seed=AI&backgroundColor=ff9800&bold=true";
    const userAvatar =
        "https://api.dicebear.com/7.x/initials/svg?seed=Faluplast&backgroundColor=1e88e5&bold=true";

    // Animation variants for the minimize effect
    const chatVariants = {
        open: {
            opacity: 1,
            scale: 1,
            x: 0,
            y: 0,
            transition: { duration: 0.4 },
        },
        minimized: {
            opacity: 0,
            scale: 0,
            x: "100vw", // Move to the right side of the screen
            y: "100vh", // Move to the bottom of the screen
            transition: { duration: 0.6 },
        },
    };

    // Add state for feedback
    const [feedbackGiven, setFeedbackGiven] = useState(new Map());
    const [feedbackMessage, setFeedbackMessage] = useState(new Map());

    // Remove the messages state and updateMessages function
    // Instead, track message indexes for feedback purposes only
    const processedMessagesRef = useRef(new Set());

    // Add these new states after other state declarations
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [currentFeedbackIndex, setCurrentFeedbackIndex] = useState(null);

    // Send initial message when component mounts
    useEffect(() => {
        if (initialMessage && !initialMessageSentRef.current) {
            // Small delay to ensure chat is ready
            const timeoutId = setTimeout(() => {
                if (chatRef.current && chatRef.current.sendMessage) {
                    chatRef.current.sendMessage(
                        initialMessage,
                        !initialMessageIsSilent
                    );
                    initialMessageSentRef.current = true;
                }
            }, 500);

            return () => clearTimeout(timeoutId);
        }
    }, [initialMessage, initialMessageIsSilent]);

    // Function to handle minimizing
    const handleMinimize = () => {
        setIsMinimized(true);
    };

    // Function to handle maximizing
    const handleMaximize = () => {
        setIsMinimized(false);
    };

    // Typing dots animation component
    const TypingDots = () => (
        <div className="flex space-x-2 p-3 bg-gray-200 text-gray-800 rounded-lg">
            <div
                className="w-2 h-2 bg-gray-600 rounded-full animate-bounce"
                style={{ animationDelay: "0ms" }}
            ></div>
            <div
                className="w-2 h-2 bg-gray-600 rounded-full animate-bounce"
                style={{ animationDelay: "200ms" }}
            ></div>
            <div
                className="w-2 h-2 bg-gray-600 rounded-full animate-bounce"
                style={{ animationDelay: "400ms" }}
            ></div>
        </div>
    );

    // Modify renderUserMessage to track user messages
    const renderUserMessage = useCallback(
        (msg, index) => {
            return (
                <div
                    key={index}
                    className="flex justify-end items-end chat-message"
                >
                    <div className="p-3 rounded-lg bg-blue-500 text-white max-w-3/4">
                        <ReactMarkdown
                            remarkPlugins={[gfm]}
                            rehypePlugins={[rehypeSanitize]}
                        >
                            {msg.message}
                        </ReactMarkdown>
                    </div>
                    <img
                        src={userAvatar}
                        alt="User Avatar"
                        className="w-8 h-8 ml-1 self-end rounded-full"
                    />
                </div>
            );
        },
        [userAvatar]
    );

    // Modify the FeedbackModal component
    const FeedbackModal = ({ isOpen, onClose, onSubmit }) => {
        const [localResponse, setLocalResponse] = useState("");
        const [submitStatus, setSubmitStatus] = useState(null); // 'submitting', 'success', 'error'

        useEffect(() => {
            if (!isOpen) {
                setLocalResponse("");
                setSubmitStatus(null);
            }
        }, [isOpen]);

        const handleSubmit = async () => {
            setSubmitStatus("submitting");
            try {
                await onSubmit(localResponse);
                setSubmitStatus("success");
                setLocalResponse("");
                onClose();
            } catch (error) {
                setSubmitStatus("error");
                // Reset error state after 2 seconds
                setTimeout(() => setSubmitStatus(null), 2000);
            }
        };

        if (!isOpen) return null;

        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 relative"
                >
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">
                            Help Us Improve
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <FontAwesomeIcon
                                icon={faXmark}
                                className="h-5 w-5"
                            />
                        </button>
                    </div>
                    <p className="text-gray-600 mb-4">
                        Please provide the correct response that would have been
                        more helpful:
                    </p>
                    <textarea
                        value={localResponse}
                        onChange={(e) => setLocalResponse(e.target.value)}
                        className="w-full h-32 p-3 border rounded-lg mb-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Type the correct response here..."
                        autoFocus
                        disabled={
                            submitStatus === "submitting" ||
                            submitStatus === "success"
                        }
                    />
                    <div className="flex justify-end space-x-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:text-gray-800"
                            disabled={
                                submitStatus === "submitting" ||
                                submitStatus === "success"
                            }
                        >
                            Cancel
                        </button>
                        <motion.button
                            onClick={handleSubmit}
                            className={`px-4 py-2 rounded-lg flex items-center justify-center min-w-[100px] ${
                                submitStatus === "success"
                                    ? "bg-green-500 hover:bg-green-600"
                                    : submitStatus === "error"
                                    ? "bg-red-500 hover:bg-red-600"
                                    : "bg-blue-600 hover:bg-blue-700"
                            } text-white`}
                            disabled={
                                !localResponse.trim() ||
                                submitStatus === "submitting" ||
                                submitStatus === "success"
                            }
                            whileHover={{ scale: submitStatus ? 1 : 1.02 }}
                            whileTap={{ scale: submitStatus ? 1 : 0.98 }}
                        >
                            <AnimatePresence mode="wait">
                                {submitStatus === "submitting" && (
                                    <motion.div
                                        key="submitting"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faSpinner}
                                            className="h-5 w-5 animate-spin"
                                        />
                                    </motion.div>
                                )}
                                {submitStatus === "success" && (
                                    <motion.div
                                        key="success"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        exit={{ scale: 0 }}
                                        className="flex items-center"
                                    >
                                        <FontAwesomeIcon
                                            icon={faCheck}
                                            className="h-5 w-5 mr-2"
                                        />
                                        Submitted!
                                    </motion.div>
                                )}
                                {submitStatus === "error" && (
                                    <motion.div
                                        key="error"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        exit={{ scale: 0 }}
                                        className="flex items-center text-white"
                                    >
                                        <FontAwesomeIcon
                                            icon={faXmark}
                                            className="h-5 w-5 mr-2"
                                        />
                                        Error
                                    </motion.div>
                                )}
                                {!submitStatus && (
                                    <motion.span
                                        key="submit"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                    >
                                        Submit Feedback
                                    </motion.span>
                                )}
                            </AnimatePresence>
                        </motion.button>
                    </div>

                    {/* Status message for errors */}
                    <AnimatePresence>
                        {submitStatus === "error" && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 10 }}
                                className="absolute bottom-0 left-0 right-0 p-4 bg-red-100 text-red-600 text-sm rounded-b-lg text-center"
                            >
                                Failed to submit feedback. Please try again.
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </motion.div>
        );
    };

    // Modify the handleFeedback function
    const handleFeedback = useCallback(
        async (messageId, isPositive, messageText, prevMessageText) => {
            setFeedbackGiven((prev) =>
                new Map(prev).set(messageId, isPositive)
            );

            if (isPositive) {
                try {
                    // We now pass message content directly
                    const userQuestion = prevMessageText || "";
                    const botResponse = messageText || "";

                    // Validate we have both messages before sending
                    if (!userQuestion || !botResponse) {
                        console.error("Missing question or response:", {
                            userQuestion,
                            botResponse,
                        });
                        return;
                    }

                    console.log("Sending feedback:", {
                        question: userQuestion,
                        response: botResponse,
                    });

                    const response = await fetch(
                        "http://localhost:3000/api/crawler/feedbacks",
                        {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                                question: userQuestion,
                                response: botResponse,
                                helpful: isPositive,
                            }),
                        }
                    );

                    if (response.ok) {
                        setFeedbackMessage((prev) =>
                            new Map(prev).set(messageId, true)
                        );
                        setTimeout(() => {
                            setFeedbackMessage((prev) => {
                                const newMap = new Map(prev);
                                newMap.delete(messageId);
                                return newMap;
                            });
                        }, 3000);
                    } else {
                        console.error(
                            "Failed to save feedback:",
                            await response.text()
                        );
                    }
                } catch (error) {
                    console.error("Error saving feedback:", error);
                }
            } else {
                // For negative feedback, show the modal and store the message texts
                setCurrentFeedbackIndex({
                    id: messageId,
                    botMessage: messageText,
                    userMessage: prevMessageText,
                });
                setShowFeedbackModal(true);
            }
        },
        []
    );

    // Modify the handleFeedbackSubmit function
    const handleFeedbackSubmit = async (correctResponse) => {
        try {
            // Extract data from the stored feedback index
            const userQuestion = currentFeedbackIndex?.userMessage || "";
            const botResponse = currentFeedbackIndex?.botMessage || "";

            const response = await fetch(
                "http://localhost:3000/api/crawler/feedbacks",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        question: userQuestion,
                        response: botResponse,
                        correctResponse: correctResponse,
                        helpful: false,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error("Failed to submit feedback");
            }

            setFeedbackMessage((prev) =>
                new Map(prev).set(currentFeedbackIndex.id, true)
            );
            setTimeout(() => {
                setFeedbackMessage((prev) => {
                    const newMap = new Map(prev);
                    newMap.delete(currentFeedbackIndex.id);
                    return newMap;
                });
            }, 3000);

            return Promise.resolve(); // Successful submission
        } catch (error) {
            console.error("Error submitting feedback:", error);
            return Promise.reject(error); // Failed submission
        }
    };

    // Store the current user message for feedback purposes
    const [lastUserMessage, setLastUserMessage] = useState("");

    // Track chat messages for feedback
    useEffect(() => {
        // When a user message is sent, store it for feedback purposes
        const handleUserMessage = (e) => {
            if (e.detail && e.detail.message) {
                setLastUserMessage(e.detail.message);
            }
        };

        window.addEventListener("user-message-sent", handleUserMessage);
        return () =>
            window.removeEventListener("user-message-sent", handleUserMessage);
    }, []);

    // Modify renderBotMessage to use direct message reference
    const renderBotMessage = useCallback(
        (msg, index) => {
            // Get the previous message from the DOM if needed
            const getPreviousMessage = () => {
                // Find previous message in the DOM
                const msgElements = document.querySelectorAll(".chat-message");
                const currentMsgEl = Array.from(msgElements).find(
                    (el) => el.dataset.index === index.toString()
                );

                // If we can't find the element, use the last known user message
                if (!currentMsgEl || !currentMsgEl.previousElementSibling) {
                    return lastUserMessage;
                }

                // Extract the text from the previous message
                const prevMsgEl = currentMsgEl.previousElementSibling;
                return prevMsgEl.textContent || lastUserMessage;
            };

            return (
                <div
                    key={index}
                    className="flex flex-col chat-message"
                    data-index={index}
                >
                    <div className="flex justify-start items-end">
                        <img
                            src={botAvatar}
                            alt="Bot Avatar"
                            className="w-8 h-8 mr-1 self-end rounded-full"
                        />
                        <div className="p-3 rounded-lg bg-gray-200 text-gray-800 max-w-4xl">
                            <ReactMarkdown
                                remarkPlugins={[gfm]}
                                rehypePlugins={[rehypeSanitize]}
                                components={{
                                    a: ({ node, ...props }) => (
                                        <a
                                            {...props}
                                            className="font-bold underline text-blue-600 hover:text-blue-800"
                                            target="_blank"
                                        />
                                    ),
                                }}
                            >
                                {msg.message}
                            </ReactMarkdown>
                        </div>
                    </div>

                    {/* Feedback Section */}
                    <div className="flex items-center ml-10 mt-2">
                        {!feedbackGiven.has(index) ? (
                            <>
                                <span className="text-sm text-gray-500 mr-2">
                                    Was this helpful?
                                </span>
                                <button
                                    onClick={() =>
                                        handleFeedback(
                                            index,
                                            true,
                                            msg.message,
                                            getPreviousMessage()
                                        )
                                    }
                                    className="p-1 px-2 rounded-lg mr-2 transition-colors duration-200 hover:bg-gray-100 text-gray-600 hover:text-green-600"
                                    title="This was helpful"
                                >
                                    <FontAwesomeIcon
                                        icon={faThumbsUp}
                                        className="h-4 w-4"
                                    />
                                </button>
                                <button
                                    onClick={() =>
                                        handleFeedback(
                                            index,
                                            false,
                                            msg.message,
                                            getPreviousMessage()
                                        )
                                    }
                                    className="p-1 px-2 rounded-lg transition-colors duration-200 hover:bg-gray-100 text-gray-600 hover:text-red-600"
                                    title="This was not helpful"
                                >
                                    <FontAwesomeIcon
                                        icon={faThumbsDown}
                                        className="h-4 w-4"
                                    />
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    disabled
                                    className={`p-1 px-2 rounded-lg mr-2 ${
                                        feedbackGiven.get(index) === true
                                            ? "bg-green-100 text-green-600"
                                            : ""
                                    }`}
                                >
                                    <FontAwesomeIcon
                                        icon={faThumbsUp}
                                        className="h-4 w-4"
                                    />
                                </button>
                                <button
                                    disabled
                                    className={`p-1 px-2 rounded-lg ${
                                        feedbackGiven.get(index) === false
                                            ? "bg-red-100 text-red-600"
                                            : ""
                                    }`}
                                >
                                    <FontAwesomeIcon
                                        icon={faThumbsDown}
                                        className="h-4 w-4"
                                    />
                                </button>
                            </>
                        )}

                        {/* Thank you message */}
                        {feedbackMessage.has(index) && (
                            <motion.span
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0 }}
                                className={`ml-4 text-sm ${
                                    feedbackGiven.get(index)
                                        ? "text-green-600"
                                        : "text-gray-600"
                                }`}
                            >
                                {feedbackGiven.get(index)
                                    ? "Thank you! We've saved this conversation as a good example."
                                    : "Thank you for your feedback!"}
                            </motion.span>
                        )}
                    </div>
                </div>
            );
        },
        [
            botAvatar,
            feedbackGiven,
            feedbackMessage,
            handleFeedback,
            lastUserMessage,
        ]
    );

    // Custom input renderer
    const renderInput = useCallback(
        ({ value, onChange, onSend, disabled, ref }) => {
            return (
                <div className="p-4 bg-white border-t flex">
                    <textarea
                        ref={ref}
                        className="flex-grow p-2 border rounded-lg"
                        rows="4"
                        placeholder={
                            settings.inputPlaceholder || "Type your message..."
                        }
                        value={value}
                        onChange={onChange}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                onSend();
                            }
                        }}
                        disabled={disabled}
                        autoFocus={!disabled}
                    />
                    <button
                        onClick={() => {
                            setIsSending(true);
                            onSend();
                            setTimeout(() => setIsSending(false), 300); // Simple visual feedback
                        }}
                        className="md:w-20 lg:w-40 ml-3 p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center"
                    >
                        {isSending ? (
                            <Loader2 className="md:h-5 md:w-5 lg:h-10 lg:w-10 animate-spin" />
                        ) : (
                            <FontAwesomeIcon
                                className="md:h-5 md:w-5 lg:h-10 lg:w-10"
                                icon={faPaperPlane}
                            />
                        )}
                    </button>
                </div>
            );
        },
        [isSending]
    );

    // Custom container renderer
    const renderContainer = useCallback(
        ({
            messages: renderedMessages,
            currentResponse,
            input,
            chatState,
            chatContainerRef,
        }) => {
            const isTyping = chatState?.busy;
            const streamedResponse = currentResponse
                ? currentResponse.split("\n").filter((line) => line.trim())
                : [];

            return (
                <>
                    {/* Chat Button (Shown When Minimized) */}
                    <motion.button
                        className="lg:w-20 lg:h-20 md:w-10 md:h-10 fixed bottom-4 right-4 bg-blue-600 text-white p-4 rounded-full shadow-lg flex justify-center items-center z-50"
                        onClick={handleMaximize}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{
                            opacity: isMinimized ? 1 : 0,
                            scale: isMinimized ? 1 : 0,
                            transition: { delay: isMinimized ? 0.3 : 0 },
                        }}
                    >
                        <FontAwesomeIcon
                            className="lg:h-10 lg:w-10 md:h-5 md:w-5"
                            icon={faCommentDots}
                        />
                    </motion.button>

                    {/* Chat Window */}
                    <motion.div
                        className="fixed inset-0 flex flex-col bg-white shadow-lg"
                        initial="open"
                        animate={isMinimized ? "minimized" : "open"}
                        variants={chatVariants}
                        style={{ originX: 1, originY: 1 }}
                    >
                        {/* Header */}
                        <div className="flex justify-between items-center bg-blue-600 text-white p-4">
                            <div className="flex flex-col">
                                <h1 className="text-xl font-semibold">
                                    {headerTitle}
                                </h1>
                                {headerDescription && (
                                    <p className="text-sm text-white opacity-90">
                                        {headerDescription}
                                    </p>
                                )}
                            </div>
                            <div className="flex flex-row items-center space-x-3">
                                {/* Minimize Button */}
                                <button
                                    onClick={handleMinimize}
                                    className="text-white hover:text-gray-300 flex items-center justify-center h-6"
                                    title="Minimize chat window"
                                >
                                    <FontAwesomeIcon
                                        icon={faWindowMinimize}
                                        className="h-4 w-4"
                                    />
                                </button>
                            </div>
                        </div>

                        {/* Chat Messages */}
                        <div
                            ref={chatContainerRef}
                            className="flex-1 overflow-y-auto p-4 space-y-4"
                        >
                            {renderedMessages}
                            {currentResponse && streamedResponse.length > 0 && (
                                <div className="flex justify-start items-end">
                                    <img
                                        src={botAvatar}
                                        alt="Bot Avatar"
                                        className="w-8 h-8 mr-1 self-end rounded-full"
                                    />
                                    <div className="p-3 rounded-lg bg-gray-200 text-gray-800 max-w-3/4">
                                        {streamedResponse.map((line, i) => (
                                            <ReactMarkdown
                                                key={i}
                                                remarkPlugins={[gfm]}
                                                rehypePlugins={[rehypeSanitize]}
                                            >
                                                {line}
                                            </ReactMarkdown>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {isTyping && !streamedResponse.length && (
                                <div className="flex justify-start">
                                    <TypingDots />
                                </div>
                            )}
                        </div>

                        {/* Input Field */}
                        {input}
                    </motion.div>
                </>
            );
        },
        [isMinimized, handleMaximize, handleMinimize, botAvatar]
    );

    return (
        <>
            <FeedbackModal
                isOpen={showFeedbackModal}
                onClose={() => {
                    setShowFeedbackModal(false);
                    setCurrentFeedbackIndex(null);
                }}
                onSubmit={handleFeedbackSubmit}
            />
            <Chat
                ref={chatRef}
                apiBaseUrl={apiBaseUrl}
                botId={botId}
                initialMessage=""
                initialMessageIsSilent={initialMessageIsSilent}
                chatContext={chatContext}
                renderUserMessage={renderUserMessage}
                renderBotMessage={renderBotMessage}
                renderInput={renderInput}
                renderContainer={renderContainer}
                persistConnection={true}
                showPrivacyBanner={false}
            />
        </>
    );
};

export default ChatWindow;
